import { useState } from 'react'
import { Card, Upload, Button, Table, Tag, Progress, Space, message, Alert, Steps, Row, Col, Statistic } from 'antd'
import { 
  UploadOutlined, 
  FolderOutlined, 
  FilePdfOutlined,
  FileWordOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  SettingOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  EyeOutlined,
  DownloadOutlined
} from '@ant-design/icons'
import type { BiddingCase } from '../../types/index'
import { mockCases } from '../../services/mockData'

const { Step } = Steps
const { Dragger } = Upload

const UploadManager = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [cases, setCases] = useState<BiddingCase[]>(mockCases)

  const uploadProps = {
    name: 'file',
    multiple: true,
    action: '/api/upload',
    accept: '.pdf,.docx,.doc',
    onChange(info: any) {
      const { status } = info.file
      if (status === 'uploading') {
        setUploading(true)
      }
      if (status === 'done') {
        message.success(`${info.file.name} 上传成功`)
        setUploading(false)
      } else if (status === 'error') {
        message.error(`${info.file.name} 上传失败`)
        setUploading(false)
      }
    },
  }

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      uploaded: { color: 'default', icon: <UploadOutlined />, text: '已上传' },
      pairing: { color: 'processing', icon: <SyncOutlined spin />, text: '配对中' },
      paired: { color: 'blue', icon: <CheckCircleOutlined />, text: '已配对' },
      cleaning: { color: 'cyan', icon: <SyncOutlined spin />, text: '清洗中' },
      cleaned: { color: 'green', icon: <CheckCircleOutlined />, text: '已清洗' },
      qc_pending: { color: 'orange', icon: <WarningOutlined />, text: '待质检' },
      qc_passed: { color: 'success', icon: <CheckCircleOutlined />, text: '质检通过' },
      qc_failed: { color: 'error', icon: <CloseCircleOutlined />, text: '质检失败' },
    }
    const cfg = config[status] || config.uploaded
    return <Tag color={cfg.color} icon={cfg.icon}>{cfg.text}</Tag>
  }

  const columns = [
    {
      title: '案件ID',
      dataIndex: 'caseId',
      key: 'caseId',
      render: (text: string) => <span className="font-mono">{text}</span>,
    },
    {
      title: '项目名称',
      dataIndex: 'projectName',
      key: 'projectName',
      ellipsis: true,
    },
    {
      title: '质疑函',
      key: 'questionDoc',
      render: (_: any, record: BiddingCase) => (
        record.questionDoc ? (
          <Space>
            <FilePdfOutlined className="text-red-500" />
            <span className="text-sm">{record.questionDoc.fileName}</span>
            {record.questionDoc.cleaningStatus === 'completed' ? (
              <CheckCircleOutlined className="text-green-500" />
            ) : record.questionDoc.cleaningStatus === 'processing' ? (
              <Progress percent={record.questionDoc.cleaningProgress} style={{ width: 60 }} />
            ) : (
              <Tag>待清洗</Tag>
            )}
          </Space>
        ) : (
          <Tag color="error">缺失</Tag>
        )
      ),
    },
    {
      title: '答复函',
      key: 'replyDoc',
      render: (_: any, record: BiddingCase) => (
        record.replyDoc ? (
          <Space>
            <FilePdfOutlined className="text-red-500" />
            <span className="text-sm">{record.replyDoc.fileName}</span>
            {record.replyDoc.cleaningStatus === 'completed' ? (
              <CheckCircleOutlined className="text-green-500" />
            ) : record.replyDoc.cleaningStatus === 'processing' ? (
              <Progress percent={record.replyDoc.cleaningProgress} style={{ width: 60 }} />
            ) : (
              <Tag>待清洗</Tag>
            )}
          </Space>
        ) : (
          <Tag color="error">缺失</Tag>
        )
      ),
    },
    {
      title: '配对状态',
      key: 'pairing',
      render: (_: any, record: BiddingCase) => (
        record.pairingInfo ? (
          <Tag color="success">
            {record.pairingInfo.method === 'auto_folder' ? '文件夹匹配' : 
             record.pairingInfo.method === 'auto_ai' ? `AI匹配(${record.pairingInfo.confidence}%)` : '手动配对'}
          </Tag>
        ) : (
          <Tag color="warning">待配对</Tag>
        )
      ),
    },
    {
      title: '整体状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: BiddingCase) => (
        <Space>
          <Button icon={<EyeOutlined />}>详情</Button>
          <Button icon={<SettingOutlined />}>配置</Button>
          {record.status === 'cleaned' && (
            <Button type="primary" icon={<PlayCircleOutlined />}>分配</Button>
          )}
        </Space>
      ),
    },
  ]

  const steps = [
    { title: '上传文件', description: '批量上传质疑函和答复函' },
    { title: '智能配对', description: '自动匹配Q&A文档对' },
    { title: '数据清洗', description: 'OCR识别和格式规范化' },
    { title: '质量检查', description: '验证数据完整性' },
    { title: '任务分配', description: '分配给标注员' },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">📤 批量上传与数据清洗管理</h1>
      </div>

      {/* 步骤指示器 */}
      <Card>
        <Steps current={currentStep}>
          {steps.map((step, index) => (
            <Step key={index} title={step.title} description={step.description} />
          ))}
        </Steps>
      </Card>

      {/* 上传区域 */}
      {currentStep === 0 && (
        <Card title="📁 第一步：文件上传">
          <Dragger {...uploadProps} className="py-8">
            <p className="text-4xl mb-4">📎</p>
            <p className="text-lg mb-2">点击或拖拽文件到此处</p>
            <p className="text-gray-500">
              支持批量上传 PDF、DOCX、DOC 格式文件，单个文件大小 ≤ 50MB
            </p>
            <p className="text-gray-400 text-sm mt-2">
              建议按项目文件夹上传，系统会自动识别配对关系
            </p>
          </Dragger>

          <div className="mt-6">
            <h4 className="font-medium mb-3">⚙️ 配对规则设置</h4>
            <Row gutter={16}>
              <Col span={8}>
                <Card className="bg-blue-50">
                  <div className="flex items-center justify-between">
                    <span>规则1: 文件夹结构</span>
                    <Tag color="blue">已启用</Tag>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">同文件夹内配对</p>
                </Card>
              </Col>
              <Col span={8}>
                <Card className="bg-blue-50">
                  <div className="flex items-center justify-between">
                    <span>规则2: 文件名相似</span>
                    <Tag color="blue">已启用</Tag>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">相似度&gt;80%配对</p>
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <div className="flex items-center justify-between">
                    <span>规则3: 文号匹配</span>
                    <Tag>备用</Tag>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">提取文号配对</p>
                </Card>
              </Col>
            </Row>
          </div>

          <div className="mt-6 flex justify-center gap-4">
            <Button type="primary" icon={<FolderOutlined />}>
              选择文件夹批量上传
            </Button>
            <Button icon={<SyncOutlined />}>
              扫描本地目录
            </Button>
            <Button icon={<DownloadOutlined />}>
              下载模板
            </Button>
          </div>
        </Card>
      )}

      {/* 统计卡片 */}
      <Row gutter={16}>
        <Col span={6}>
          <Card>
            <Statistic title="全部案件" value={cases.length} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="待配对" 
              value={cases.filter(c => !c.pairingInfo).length}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="清洗中" 
              value={cases.filter(c => c.status === 'cleaning').length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="已完成" 
              value={cases.filter(c => c.status === 'completed').length}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 案件列表 */}
      <Card 
        title="📋 项目与文档管理" 
        extra={
          <Space>
            <Button icon={<PlayCircleOutlined />}>开始自动配对</Button>
            <Button>手动配对</Button>
          </Space>
        }
      >
        <Table 
          columns={columns} 
          dataSource={cases}
          rowKey="caseId"
          pagination={{ pageSize: 10 }}
        />
      </Card>
    </div>
  )
}

export default UploadManager
